/* eslint-disable complexity */
import type { IFeatureFlagRepository } from '@delta-defense/feature-flag-service/service/iFeatureFlagRepository';
import { FunctionComponent, useEffect, useState } from 'react';
import { FeatureFlagService } from '@delta-defense/feature-flag-service';
import { ITransaction, Transaction } from '../../core/domain/module';
import { Analytics, ctaClick, IAnalytics, InternalEventNames, Steps } from '../../core/services/analytics/module';
import { RouteStepRecord } from '../../utilities/routeStepRecord';
import { safelyExecute } from '../../utilities/safelyExecute';
import { FeatureFlags } from '../../core/enums/featureFlags';
import styles from './SubmitButton.module.scss';

export type Props = {
	children: React.ReactNode;
	transaction?: ITransaction;
	analytics?: IAnalytics;
	locationPathname?: string;
	className?: string;
	onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	idSuffix?: string;
	disableStickyBottomFeatureFlag?: boolean;
	features?: IFeatureFlagRepository;
};

const SubmitButton: FunctionComponent<Props> = ({
	children,
	className,
	onClick,
	transaction = Transaction.Instance(),
	locationPathname = window.location.pathname,
	analytics = Analytics.Instance(),
	idSuffix,
	disableStickyBottomFeatureFlag = false,
	features = FeatureFlagService.Instance()
}) => {
	const [validationErrors, setValidationErrors] = useState<boolean>(true);

	useEffect(() => {
		const transactionUpdatesSub = transaction.Updates.Subscribe(() => {
			const newValidationErrors = !transaction.GetCurrentStep().Validate();
			setValidationErrors(newValidationErrors);
		});

		return () => {
			transaction.Updates.Cancel(transactionUpdatesSub);
		};
	}, []);

	const trackNecessaryEvents = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		const ctaText = (e.target as HTMLButtonElement).innerText;

		const errorFields: Record<string, string> = {};
		const currentStepFields = transaction.GetCurrentStep().Fields;
		for (const key in currentStepFields) {
			const element = currentStepFields[key];
			if (element.Validity.CurrentIssue === false) {
				errorFields[key] = element.ValidationMessage;
			}
		}

		const hasErrors = Object.keys(errorFields).length > 0;

		if (hasErrors) {
			analytics.TrackInternal({
				event_name: InternalEventNames.ValidationFail,
				data: errorFields,
				step: RouteStepRecord[locationPathname] || Steps.One
			});
		} else {
			analytics.TrackInteraction(ctaClick(ctaText, RouteStepRecord[locationPathname] || Steps.One));
		}
	};

	const stickyBottomMobileEnabled = features.IsEnabled(FeatureFlags.CheckoutMobileSticky) && !disableStickyBottomFeatureFlag;
	return (
		<>
			<div
				className={stickyBottomMobileEnabled && !disableStickyBottomFeatureFlag ? styles.bottomJustifiedContainer : styles.defaultWrapOuter}
			>
				<div className={stickyBottomMobileEnabled && !disableStickyBottomFeatureFlag ? styles.stickyButtonWrap : styles.defaultWrapInner}>
					{/* eslint-disable-next-line enforce-ids-in-jsx/missing-ids */}
					<button
						id={idSuffix ? `submitButton_${idSuffix}` : 'submitButton'}
						type="submit"
						className={`${styles.btn} ${stickyBottomMobileEnabled && !disableStickyBottomFeatureFlag ? styles.stickyBttn : ''} ${
							validationErrors ? styles.disabledBtn : ''
						} ${className ? className : ''}`}
						onClick={(e) => {
							onClick?.(e);
							if (!e.defaultPrevented) {
								trackNecessaryEvents(e);
								if (validationErrors) {
									e.preventDefault();
									safelyExecute(() => {
										Object.values(transaction.GetCurrentStep().Fields).forEach((f) => {
											f.Clean = false;
										});
										transaction.Updates.Publish(true);
									});
								}
							}
						}}
					>
						{children}
					</button>
				</div>
			</div>
		</>
	);
};

export default SubmitButton;
